import { UserInfo } from '@/components/layout/user-info';
import { Button } from '@/components/ui/button';
import { resourceRegistry } from '@/resources/resource-map';
import {
	BarChart3,
	BookOpen,
	Briefcase,
	Building,
	Car,
	ChevronRight,
	DollarSign,
	DoorOpen,
	FileSpreadsheet,
	FileText,
	FlaskConical,
	Home,
	LogOut,
	Megaphone,
	ParkingCircle,
	Plus,
	Scale,
	Settings,
	Shield,
	Ticket,
	UserCheck
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentFacility } from '@/hooks/useCurrentFacility';

interface SidebarProps {
	isOpen: boolean;
	onClose: () => void;
	isCollapsed?: boolean;
	onToggleCollapse?: () => void;
}

export interface MenuItem {
	titleKey: string;
	icon: React.ComponentType<any>;
	path?: string;
	children?: MenuItem[];
}

const normalizePath = (value: string) => {
	if (value === '/') {
		return value;
	}
	return value.replace(/\/+$/, '');
};

const isPathActive = (path: string | undefined, pathname: string) => {
	if (!path) {
		return false;
	}
	const normalizedPath = normalizePath(path);
	const normalizedLocation = normalizePath(pathname);
	if (normalizedPath === '/') {
		return normalizedLocation === '/';
	}
	return (
		normalizedLocation === normalizedPath ||
		normalizedLocation.startsWith(`${normalizedPath}/`)
	);
};

const itemMatchesPath = (item: MenuItem, pathname: string): boolean => {
	if (isPathActive(item.path, pathname)) {
		return true;
	}
	return (
		item.children?.some(child => itemMatchesPath(child, pathname)) ?? false
	);
};

const getCreatePathForListPath = (listPath?: string) => {
	if (!listPath) {
		return undefined;
	}

	for (const resource of Object.values(resourceRegistry)) {
		if (resource.list?.path === listPath) {
			return resource.create?.path;
		}
	}

	return undefined;
};

export const menuItems: MenuItem[] = [
	{
		titleKey: 'navigation:menu.dashboard',
		icon: Home,
		children: [
			{
				titleKey: 'navigation:menu.dashboard',
				icon: Home,
				path: resourceRegistry.dashboard.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.facility',
		icon: Building,
		children: [
			{
				titleKey: 'navigation:menu.parking_sessions',
				icon: ParkingCircle,
				path: resourceRegistry.parkingSessions.list?.path
			},
			{
				titleKey: 'navigation:menu.gate_monitor',
				icon: Shield,
				path: resourceRegistry.gateMonitor.list?.path
			},
			{
				titleKey: 'navigation:menu.gates',
				icon: DoorOpen,
				path: resourceRegistry.gates.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.customer_management',
		icon: Briefcase,
		children: [
			{
				titleKey: 'navigation:menu.customers',
				icon: UserCheck,
				path: resourceRegistry.customers.list?.path
			},
			{
				titleKey: 'navigation:menu.contracts',
				icon: FileText,
				path: resourceRegistry.contracts.list?.path
			},
			{
				titleKey: 'navigation:menu.vehicles',
				icon: Car,
				path: resourceRegistry.vehicles.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.pricing_management',
		icon: DollarSign,
		children: [
			{
				titleKey: 'navigation:menu.tariffs',
				icon: BookOpen,
				path: resourceRegistry.tariffs.list?.path
			},
			{
				titleKey: 'navigation:menu.limit_policies',
				icon: Scale,
				path: resourceRegistry.limitPolicies.list?.path
			},
			{
				titleKey: 'navigation:menu.pricing_policies',
				icon: FileSpreadsheet,
				path: resourceRegistry.pricingPolicies.list?.path
			},
			{
				titleKey: 'navigation:menu.scenarios',
				icon: FlaskConical,
				path: resourceRegistry.scenarios.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.marketing',
		icon: Megaphone,
		children: [
			{
				titleKey: 'navigation:menu.campaigns',
				icon: Megaphone,
				path: resourceRegistry.campaigns.list?.path
			},
			{
				titleKey: 'navigation:menu.vouchers',
				icon: Ticket,
				path: resourceRegistry.vouchers.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.reports',
		icon: BarChart3,
		children: [
			{
				titleKey: 'navigation:menu.reports',
				icon: BarChart3,
				path: resourceRegistry.reports.list?.path
			}
		]
	},
	{
		titleKey: 'navigation:menu.settings',
		icon: Settings,
		children: [
			{
				titleKey: 'navigation:menu.users',
				icon: UserCheck,
				path: resourceRegistry.users.list?.path
			},
			{
				titleKey: 'navigation:menu.settings',
				icon: Settings,
				path: '/settings'
			}
		]
	}
];

interface SidebarItemProps {
	item: MenuItem;
	level?: number;
	onItemClick?: () => void;
	isCollapsed?: boolean;
	index?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
	item,
	level = 0,
	onItemClick,
	isCollapsed = false,
	index = 0
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation('navigation');
	const hasChildren = Boolean(item.children && item.children.length > 0);
	const createPath = React.useMemo(
		() => getCreatePathForListPath(item.path),
		[item.path]
	);

	const isActive = React.useMemo(
		() => isPathActive(item.path, location.pathname),
		[item.path, location.pathname]
	);

	const itemRef = React.useRef<HTMLElement | null>(null);

	React.useEffect(() => {
		if (isActive && itemRef.current) {
			itemRef.current.scrollIntoView({
				block: 'nearest',
				inline: 'nearest',
				behavior: 'smooth'
			});
		}
	}, [isActive]);

	const handleItemClick = (
		event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
	) => {
		// Only close sidebar for primary button same-tab navigation
		if (
			onItemClick &&
			event.button === 0 &&
			!event.ctrlKey &&
			!event.metaKey &&
			!event.altKey &&
			!event.shiftKey
		) {
			onItemClick();
		}
	};

	const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
		event.stopPropagation();

		if (!item.path || !createPath) {
			return;
		}

		onItemClick?.();
		navigate(item.path);

		window.requestAnimationFrame(() => {
			navigate(createPath);
		});
	};

	return (
		<div className="relative -mx-2">
			{level === 0 ? (
				<div
					ref={node => {
						itemRef.current = node;
					}}
					className={`sentry-sidebar-item sentry-sidebar-item-root ${index === 0 ? 'pt-0' : 'pt-5'} pb-0 cursor-default pointer-events-none px-2 pl-2 ${isCollapsed ? 'justify-center' : ''}`}
					aria-current={isActive ? 'page' : undefined}
					title={
						isCollapsed && level === 0
							? t(item.titleKey)
							: undefined
					}
				>
					{!isCollapsed && (
						<span className="flex-1 text-xs font-light uppercase">
							{t(item.titleKey)}
						</span>
					)}
				</div>
			) : (
				<Link
					ref={node => {
						itemRef.current = node;
					}}
					to={item.path ?? '#'}
					className={`group sentry-sidebar-item sentry-sidebar-item-child py-3 px-4 ${isActive ? 'active' : ''}`}
					aria-current={isActive ? 'page' : undefined}
					title={isCollapsed ? t(item.titleKey) : undefined}
					onClick={handleItemClick}
				>
					<div className="relative">
						<item.icon className="h-3.5 w-3.5 flex-shrink-0 mr-3" />
					</div>
					{!isCollapsed && (
						<span className="flex-1 text-sm font-medium">
							{t(item.titleKey)}
						</span>
					)}
					{!isCollapsed && createPath && (
						<button
							type="button"
							onClick={handleAddClick}
							className="group ml-2 inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-500/30 bg-slate-800/20 text-slate-300 opacity-0 transition-all duration-200 hover:border-blue-400/50 hover:bg-blue-500/15 hover:text-blue-300 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 group-hover:opacity-100"
							aria-label="Create new"
							title="Create new"
						>
							<Plus className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:rotate-90 group-hover:scale-110 group-focus-visible:rotate-90 group-focus-visible:scale-110 motion-reduce:transform-none motion-reduce:transition-none" />
						</button>
					)}
				</Link>
			)}

			{/* Always show subitems for level 0, regular submenu for other levels */}
			{hasChildren && !isCollapsed && (
				<div className="sidebar-submenu-container mt-1 space-y-1 pl-2">
					{item.children?.map((child, index) => (
						<SidebarItem
							key={index}
							item={child}
							level={level + 1}
							onItemClick={onItemClick}
							isCollapsed={isCollapsed}
						/>
					))}
				</div>
			)}

			{/* Inline child icons for collapsed mode */}
			{isCollapsed && hasChildren && level === 0 && (
				<div className="sidebar-child-container mt-2 space-y-1 overflow-hidden">
					{item.children?.map((child, index) =>
						child.path ? (
							<Link
								key={index}
								to={child.path}
								className={`sidebar-child-icon ${
									isPathActive(child.path, location.pathname)
										? 'bg-blue-500 text-white'
										: 'text-gray-400 hover:text-white'
								}`}
								title={t(child.titleKey)}
								onClick={handleItemClick}
							>
								<child.icon />
							</Link>
						) : null
					)}
				</div>
			)}
		</div>
	);
};

export const Sidebar: React.FC<SidebarProps> = ({
	isOpen,
	onClose,
	isCollapsed = false,
	onToggleCollapse
}) => {
	const { isAuthenticated } = useAuth();
	const { facilityName } = useCurrentFacility();
	const showUserSection = isAuthenticated;

	return (
		<div
			className={`kt-sidebar ${!isOpen ? 'mobile-hidden' : ''} ${isCollapsed ? 'kt-sidebar-collapsed' : ''} h-full`}
		>
			<div className="relative flex h-full flex-col">
				{/* Logo/Brand */}
				<div className="border-b border-slate-600 p-6 dark:border-slate-700">
					<div
						className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}
					>
						<div className="flex items-center">
							<div
								className={`flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500 shadow-lg ${isCollapsed ? '' : 'mr-3'}`}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 512 512"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
									className="text-white"
								>
									<path
										d="M256 48C158.8 48 80 126.8 80 224c0 140.3 176 252 176 252s176-111.7 176-252c0-97.2-78.8-176-176-176z"
										stroke="currentColor"
										strokeWidth="28"
										strokeLinejoin="round"
									/>
									<path
										d="M186 210 L256 330 L326 210"
										stroke="currentColor"
										strokeWidth="40"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</div>
							{!isCollapsed && (
								<div>
									<h1 className="text-lg font-semibold text-white">
										Vectro Station
									</h1>
									{facilityName ? (
										<p className="text-xs font-semibold text-orange-400">
											{facilityName}
										</p>
									) : (
										<p className="text-xs text-gray-400 dark:text-[#94A3B8]">
											Parking Management
										</p>
									)}
								</div>
							)}
						</div>
						{!isCollapsed && onToggleCollapse && (
							<button
								onClick={onToggleCollapse}
								className="p-1 text-gray-400 hover:text-white transition-colors rounded"
								title="Collapse sidebar"
							>
								<ChevronRight className="h-4 w-4 rotate-180" />
							</button>
						)}
					</div>
					{isCollapsed && onToggleCollapse && (
						<div className="mt-4 flex justify-center">
							<button
								onClick={onToggleCollapse}
								className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg border border-slate-600 hover:border-slate-500"
								title="Expand sidebar"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						</div>
					)}
				</div>

				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-6 pt-5">
					{menuItems.map((item, index) => (
						<SidebarItem
							key={index}
							item={item}
							onItemClick={onClose}
							isCollapsed={isCollapsed}
							index={index}
						/>
					))}
				</nav>

				{showUserSection && (
					<SidebarUserSection isCollapsed={isCollapsed} />
				)}
			</div>
		</div>
	);
};

const SidebarUserSection = ({
	isCollapsed = false
}: {
	isCollapsed?: boolean;
}) => {
	const { logout } = useAuth();

	return (
		<div className="border-t border-slate-700/50 p-4">
			{!isCollapsed && <UserInfo />}
			<Button
				type="button"
				variant="ghost"
				size="sm"
				onClick={() => logout()}
				className={`${isCollapsed ? '' : 'mt-3'} w-full ${isCollapsed ? 'justify-center' : 'justify-start'} gap-2 text-sm font-medium text-slate-200 hover:text-white dark:text-[#94A3B8] dark:hover:text-[#F1F5F9]`}
				title={isCollapsed ? 'Logout' : undefined}
			>
				<LogOut className="h-4 w-4" />
				{!isCollapsed && 'Logout'}
			</Button>
		</div>
	);
};
